import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * Admin-side payment operations that go through Edge Functions.
 *
 * Reuses the canonical `EdgeFunctionError` from `adminCustomerService` so
 * Billing-tab UI can branch on `code` for targeted UX (e.g. show "Issue
 * invoice first" when code is `invoice_not_issued`).
 */

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const idempotencyKey = crypto.randomUUID();
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  });

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
      }
    }
    throw new Error(error.message);
  }

  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }

  return data as TRes;
}

export interface RecordManualPaymentInput {
  invoice_id: string;
  gateway: 'eft_manual' | 'staff_adjustment';
  reference?: string;
  paid_at?: string;
}

export interface RecordManualPaymentResult {
  ok: true;
  payment_id: string;
  invoice_state: 'paid';
  triggered_unsuspend: boolean;
  invoice_number: string;
}

export const adminPaymentService = {
  async recordManual(input: RecordManualPaymentInput): Promise<RecordManualPaymentResult> {
    return invokeEdgeFunction<RecordManualPaymentResult>('payment-record-manual', {
      invoice_id: input.invoice_id,
      gateway: input.gateway,
      reference: input.reference,
      paid_at: input.paid_at,
    });
  },
};
